import type { Ref } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useComposedRefs } from '@tamagui/core';
import type { TextInput as RNTextInput } from 'react-native';

import { TextInputBase } from '../shared/TextInputBase';
import type { TextInputBaseProps } from '../shared/TextInputBase';
import {
  formatInputValue,
  getFormatProps,
  getSuggestion,
  useDebounce,
  validateInputValue,
} from './helpers';

export type ValidationState = 'valid' | 'invalid' | 'unknown';

type ValidatedTextInputProps = TextInputBaseProps & {
  /** The multiline property is excluded in this type */
  multiline?: never;
  /** The value property is excluded in this type */
  value?: never;
  /** The invalid property is excluded in this type */
  invalid?: never;
  /** The autoComplete property is excluded in this type */
  autoComplete?: never;
} & {
  /**
   * Sets the keyboard type and provides formatting/validation appropriate to
   * the expected semantic meaning for the content that users enter.
   */
  format:
    | Extract<
        TextInputBaseProps['textContentType'],
        'emailAddress' | 'telephoneNumber' | 'password' | 'none'
      >
    | 'percentage'
    | 'number'
    | 'dollars';
  /**
   * Pass onValidationChange when using Forms - this allows the Form to get access to the validation state of the input
   * Helpful for form validation - so client can disable submit button if input !== "valid"
   * Inputs are validated onBlur (which changes the validation state to either 'invalid'/'valid')
   * When an onChange event is triggered the validation state is set to 'unknown' since we have not re-validated the input
   * See: test "ValidatedTextInput does not submit when invalid for example"
   */
  onValidationChange?: (validationState: ValidationState) => void;
  /**
   * Allows the client to gain access to the formatted and unformatted value of the input
   * This if you need the unformatted value for the backend
   */
  getValue?: ({
    formattedValue,
    unformattedValue,
  }: {
    /**
     * The formatted value of the input
     */
    formattedValue?: string;
    /**
     * The unformatted value of the input
     */
    unformattedValue?: string;
  }) => void;
  /**
   * Allows the input to provide suggestions to the user
   * This is only useful for email textContentTypes
   */
  allowSuggestions?: boolean;
  /**
   * Custom error messages for input - otherwise the default will be used
   */
  customErrorMessages?: {
    /**
     * Custom error message for when the field is required
     * Default: 'This field is required'
     */
    fieldRequired?: string;
    /**
     * Custom error message for when email is invalid
     * Default: 'Please enter a valid email address'
     */
    invalidEmail?: string;
    /**
     * Custom error message for when phone is invalid
     * Default: 'Please enter a valid phone number'
     */
    invalidPhoneNumber?: string;
  };
};

/**
 *`ValidatedTextInput` is a React component that provides real-time validation feedback as the user types.
 * It leverages the concept of controlled components in React, where the input value is managed by the component's state.
 * This allows `ValidatedTextInput` to perform validation checks, format and accept suggestions.
 * If you want an initial value to appear in the input, you can pass it as a `prefilledValue` prop. Please note that the input will not update if this value is changed - this is meant to be a static value only to be passed in on mount.
 * To leverage validation you must pass in a `format` prop - to help the component understand what type of input it is validating.
 *
 * The available formats are:
 *
 * - 'none' (The component will use the default keyboard. Useful if you only
 * care about, e.g., the "required" prop.)
 *
 * - 'emailAddress' (The component will check that the input looks like a valid
 * email address and provide suggestions for common typos if `allowSuggestions`
 * is true.)
 *
 * - 'telephoneNumber' (The component will format and validate the input like a
 * phone number. Phone numbers are formatted like so: (123) 456-7890)
 *
 * - 'password' (The component will treat the input like a password.)
 *
 * - 'percentage' (The input will be restricted to strings representing whole
 * numbers between 0 and 100, and the text input will display a suffix of "%"
 * when the input is non-empty. Note that it does not currently allow
 * percentages greater than 100 - let CPLAT know if you need this feature!)
 *
 * - 'number' (The input will be restricted to strings representing whole
 * numbers between 0 and 999,999,999,999,999. It will also be formatted with
 * commas.)
 *
 * - 'dollars' (Same as 'number', but it also adds a prefix of "$". Note that it
 * does not currently handle cents — let CPLAT know if you need this feature!)
 *
 * Validation happens onChange with a debounce applied to give a smooth experience for error messages as well not to interrupt the user when they are typing.
 * This component also validates and formats the prefilledValue on mount if it is passed in.
 *
 * If you want to handle validation yourself or would like to use a uncontrolled component you can use the component TextInput instead.
 *
 * Passing the optional allowSuggestions prop will show the user suggestions for the input value. We currently only show suggestions for email types.
 *
 *
 * When using this in a form, you can use the onValidationChange prop to keep track of the validationState of the input in your component.
 *
 * 1. Keep track of the validationState of the input in your component:
 * `const [validationState, setValidationState] = useState<'valid' | 'invalid' | 'unknown'>('unknown');`
 * 2. Pass setValidationState to ValidatedTextInput as the onValidationChange prop:
 * 3. Disable the submit button if the input is not valid (validationState !== 'valid'). (unknown means we have not validated the input yet and invalid means the input is invalid)
 * Look at ValidatedTextInput.spec.tsx test: "validatedTextInput does not submit when invalid" for an example
 *
 *
 *
 * You must pass a `title` prop. This is so we can ensure that the input meets
 * accessibility requirements for screen readers.
 *
 * ValidatedTextInput always takes up the full width of its container. To limit its
 * width, wrap it in a Box or other container component.
 *
 * If you want to track impression events ("inputViewEvent"), pass
 * `analyticsName` and `trackImpression`.
 *
 * ValidatedTextInput supports a subset of [React Native's TextInput props](
 * https://reactnative.dev/docs/textinput). One notable prop is [autoFocus](https://reactnative.dev/docs/textinput#autofocus) — be careful
 * when using `autoFocus` because it can cause confusing behavior. Automatically
 * focusing on a control can make the page scroll on load and cause users to
 * miss context created by content earlier on the page. It can also cause
 * dynamic keyboards to display over the page on touch devices. For
 * screen-readers, it can "teleport" the user to the form control without
 * warning them or announcing anything on the page before the control's label
 */
const ValidatedTextInput = forwardRef(function ValidatedTextInput(
  {
    onBlur,
    format,
    required = false,
    prefilledValue,
    onChangeText,
    onValidationChange,
    allowSuggestions,
    /**
     * Passing errorText will immediately invoke an error - this is useful to trigger an error on things like async server errors
     * In most cases you will want ValidateTextInput to handle validation/error messsages for you
     * In cases where you want to customize your error message use customErrorMessages instead - the error message will then show based on the error type
     */
    errorText,
    customErrorMessages,
    getValue,
    ...props
  }: ValidatedTextInputProps,
  ref: Ref<RNTextInput>,
) {
  const [validationState, setValidationState] = useState<ValidationState>('unknown');
  const [currentValue, setCurrentValue] = useState<string>(prefilledValue || '');
  const [suggestion, setSuggestion] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState(errorText);

  const inputRef = useRef<RNTextInput | HTMLInputElement>(null);

  const combinedInputRef = useComposedRefs(ref, inputRef);

  const formatProps = useMemo(
    () => getFormatProps({ format, value: currentValue }),
    [format, currentValue],
  );

  const handleValidationChange = useCallback(
    (currentErrorMessage?: string) => {
      setErrorMessage(currentErrorMessage);
      const newValidationState = currentErrorMessage ? 'invalid' : 'valid';
      setValidationState(newValidationState);
      // allows the client access to the validation state
      onValidationChange && onValidationChange(newValidationState);
    },
    [onValidationChange],
  );

  // Validates the input to update the validation state
  const validateInput = useCallback(
    async (value: string) => {
      validateInputValue(handleValidationChange, value, format, required, customErrorMessages);
      if (allowSuggestions) {
        const suggestedValue = await getSuggestion(value, format);
        setSuggestion(suggestedValue);
      }
    },
    [format, required, allowSuggestions, customErrorMessages, handleValidationChange],
  );

  //Validates the input after a debounce (hook clears its timer on unmount)
  const debouncedValidateInput = useDebounce(validateInput, 1000);

  const handleBlur = (e: Parameters<NonNullable<typeof onBlur>>[0]) => {
    validateInput(currentValue);
    onBlur && onBlur(e);
  };

  // Keep track of the cursor position (just used on native)
  const selectionRef = useRef({ start: 0, end: 0 });
  const handleSelectionChange: TextInputBaseProps['onSelectionChange'] = ({
    nativeEvent: { selection },
  }) => {
    selectionRef.current = selection;
  };

  const handleChange = useCallback(
    (text: string) => {
      const formattedValue = formatInputValue(text, format, getValue);
      setCurrentValue(formattedValue);
      setErrorMessage('');
      // this shows we need to validated the input
      setValidationState('unknown');
      onValidationChange && onValidationChange('unknown');
      onChangeText && onChangeText(formattedValue);
      debouncedValidateInput(text);

      if (formattedValue !== text) {
        //Handle Cursor position after formatting
        const inputElement = inputRef.current;
        if (inputElement) {
          // Calculate the change in the cursor position
          const currentTextLength = text.length;
          const formattedTextLength = formattedValue.length;
          const textLengthChange = formattedTextLength - currentTextLength;

          // Method for getting and setting the cursor position varies by platform
          if ('selectionStart' in inputElement) {
            handleCursorUpdateWeb(inputElement, textLengthChange);
          }
        }
      }
    },
    [format, onChangeText, onValidationChange, getValue, debouncedValidateInput],
  );

  // Validates and formats the initialValue on mount
  useEffect(() => {
    if (prefilledValue) {
      validateInputValue(
        handleValidationChange,
        prefilledValue,
        format,
        required,
        customErrorMessages,
      );
      setCurrentValue(formatInputValue(prefilledValue, format, getValue));
    }
    // only run this on the initialmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revalidate the input when the format changes
  const prevFormat = useRef(format);
  useEffect(() => {
    const revalidate = () => {
      validateInputValue(
        handleValidationChange,
        currentValue,
        format,
        required,
        customErrorMessages,
      );
      setCurrentValue(formatInputValue(currentValue, format, getValue));
    };
    if (format !== prevFormat.current) {
      revalidate();
    }
    prevFormat.current = format;
  }, [format, handleValidationChange, currentValue, required, customErrorMessages, getValue]);

  return (
    <TextInputBase
      {...formatProps}
      {...props}
      ref={combinedInputRef}
      required={required}
      errorText={errorMessage || errorText}
      onChangeText={handleChange}
      value={currentValue}
      onBlur={handleBlur}
      invalid={validationState === 'invalid'}
      suggestion={suggestion}
      onSuggestion={() => {
        if (suggestion) {
          setCurrentValue(suggestion);
          setSuggestion(undefined);
          validateInput(suggestion);
        }
      }}
      onSelectionChange={handleSelectionChange}
    />
  );
});

// On web, get the cursor position from inputElement.selectionStart and update
// it during the next animation frame
const handleCursorUpdateWeb = (inputElement: HTMLInputElement, textLengthChange: number) => {
  const cursorPosition = inputElement.selectionStart;
  if (cursorPosition !== null) {
    const newCursorPosition = cursorPosition + textLengthChange;
    window.requestAnimationFrame(() => {
      inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
    });
  }
};
export { ValidatedTextInput };
export type { ValidatedTextInputProps };
